// This is the initial implementation of our TextField component, this should be the only place imported from MUI
// // eslint-disable-next-line no-restricted-imports
import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

type WarningProps = {
  warning?: boolean;
  warningText?: string;
};

export interface SharedTextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
> extends WarningProps {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
}

interface ControlledTextFieldProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<MuiTextFieldProps, 'name'>, SharedTextFieldProps<TFieldValues> {
  /**
   * Optional transform function that can be used to modify the input value before it's set in the form state.
   * @value The current input value from the TextField's onChange event.
   * @returns The transformed string value that will be set in the form state.
   */
  transform?: (value: string) => string;
}

export const TextField = ({
  warning,
  warningText,
  errorMsg,
  helperText: helperTextProp,
  ...props
}: MuiTextFieldProps & WarningProps & { errorMsg?: string }) => {
  const helperText = errorMsg ?? (warning && warningText ? warningText : helperTextProp);
  const themeColor = props.color ?? 'primary';

  return (
    <MuiTextField
      {...props}
      color={warning && !props.error ? 'warning' : themeColor}
      helperText={helperText}
    />
  );
};

export const ControlledTextField = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  transform = (value) => value,
  ...textFieldProps
}: ControlledTextFieldProps<TFieldValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          {...textFieldProps}
          onChange={(e) => field.onChange(transform(e.target.value))}
          error={!!fieldState.error}
          errorMsg={fieldState.error?.message}
        />
      )}
    />
  );
};

export default ControlledTextField;
